import { path } from 'ramda';
import React from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import { SecurityLabel } from '@ui/labels/SecurityLabel.tsx';
import Tooltip from '@ui/tooltip';
import { getPoiIconId } from '@utils/poi.ts';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import { handleKeyboardSelect } from '../../../../utils/keyboardUtils.ts';
import HandlerContext from '../handlerContext.tsx';

const Wrapper = styled.div`
  .-mobile & {
    margin-bottom: 10px;
  }
`;

const TopItemsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const CloseButtonWrapper = styled.div`
  align-self: flex-start;
`;

const CloseButton = styled.div`
  cursor: pointer;
  width: 32px;
  height: 32px;

  &:hover > .tooltip {
    top: 31px;
    display: block;
    position: absolute;
  }
`;

const PoiIcon = styled.div`
  margin-inline-end: 14px;
  width: 36px;
  height: 36px;
`;

const PoiTitle = styled.h1`
  && {
    ${({ theme }) => theme.fontSize('20px')};
    margin: 0px;
    color: var(--color-primaryText);
    font-weight: 500;
    line-height: 26px;
    flex: 1 1 auto;
  }
`;

const SecurityWaitLabelStyled = styled(SecurityLabel)`
  margin-block: 2px 0;
  margin-inline: 52px 0;
`;

const PoiHeader = ({ poi, T, className }) => {
  const colors = useCSSColors();
  if (!poi) {
    return null;
  }

  const dynamicData = path(['dynamicData', 'security'], poi);

  const statusText = dynamicData?.isTemporarilyClosed
    ? T('ui:Closed')
    : T('ui:_xx_ minute wait', { count: dynamicData?.queueTime });

  return (
    <HandlerContext.Consumer>
      {ctx => (
        <Wrapper className={className}>
          <TopItemsWrapper>
            <PoiIcon>
              <Icon name={getPoiIconId(poi)} cdn width={36} height={36} />
            </PoiIcon>
            <PoiTitle data-testid="PoiHeader">{poi.name}</PoiTitle>
            <CloseButtonWrapper>
              <Tooltip text={T('poiView:Close')} arrowPosition="left">
                <CloseButton
                  onClick={ctx.onCloseClicked}
                  data-testid="closeButton"
                  aria-label={T('poiView:Close')}
                  role="button"
                  tabIndex="0"
                  onKeyDown={handleKeyboardSelect(() => ctx.onCloseClicked())}
                >
                  <Icon name="close" cdn width={32} height={32} fillColor={colors.secondary} />
                </CloseButton>
              </Tooltip>
            </CloseButtonWrapper>
          </TopItemsWrapper>
          <SecurityWaitLabelStyled poi={poi} T={T} statusText={statusText} />
        </Wrapper>
      )}
    </HandlerContext.Consumer>
  );
};

export default PoiHeader;
